import React, { useEffect, useRef } from "react";
import tailwindClassnames from "./TailwindClassnames";
import { isTopLayerOpen } from "./topLayer";

/**
 * Elements a dialog's Tab trap and initial-focus logic consider focusable.
 *
 * The same string `ReportProblemModal`, `RolePermissionsInfo` and `MigrateFromWildlifeInsights`
 * each hold privately; exported from here so the next dialog does not need a fourth copy. The
 * `tabIndex >= 0` filter in `focusableWithin` matters alongside it: the panel itself carries
 * `tabIndex={-1}` so it can take focus, and a hidden file input does the same, and neither
 * may join the Tab cycle.
 *
 * `summary` is here because browsers put a `<details>` disclosure's summary in the Tab order.
 * Left out, the trap treated a Tab that had landed on one as focus outside the dialog and sent
 * it back to the first control, so in Import from CSV's report (two `<details>`) forward Tab
 * looped × → "File details" → × and never reached the Import button (AKL-883, QA Swarm UX-001).
 * Any other natively tabbable element a dialog adds has to be listed here for the same reason.
 */
export const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])';

/**
 * The controls inside `panel` that a Tab press can land on, in document order.
 * @param panel the dialog panel
 * @returns enabled, tabbable descendants of the panel
 */
export function focusableWithin(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);
}

/**
 * The panels currently mounted, in mount order. The document-level fallback listener below
 * belongs to the last one only, so two dialogs open at once (a confirm over a form) do not
 * both answer a key that reached neither of them.
 */
const mountedPanels: HTMLElement[] = [];

/** The subset of a keyboard event the dialog's key handling reads, shared by React and DOM events. */
type KeyLike = {
  key: string;
  shiftKey: boolean;
  target: EventTarget | null;
  preventDefault: () => void;
  stopPropagation: () => void;
};

type ModalDialogLabel =
  | {
      /** The id of the visible title; the dialog is named by it via `aria-labelledby`. */
      labelledBy: string;
      label?: never;
    }
  | {
      /** A name for a dialog with no visible title. Prefer `labelledBy` whenever there is one. */
      label: string;
      labelledBy?: never;
    };

type ModalDialogProps = ModalDialogLabel & {
  /** Called on Escape. The same callback the dialog's × and Cancel buttons call. */
  onClose: () => void;
  /**
   * True while the dialog's primary action is in flight. Escape then does nothing: the request
   * is a write, and closing the dialog would hide its outcome. (Whether the × and Cancel
   * buttons are also disabled in that state is each consumer's own call, and today they
   * differ; this prop only governs the keyboard.)
   */
  busy?: boolean;
  /** The overlay's stacking class, e.g. `z-50` — per consumer, as `TailwindClassnames` documents. */
  overlayClassName: string;
  /** The panel's width class, e.g. `max-w-md` — per consumer, as `TailwindClassnames` documents. */
  panelClassName: string;
  /** `data-testid` for the panel. */
  testId?: string;
  /**
   * Where focus goes on close when nothing useful had focus as the dialog opened (focus was on
   * `<body>`). For a dialog opened asynchronously by a control that was disabled while its
   * request ran: by the time the dialog mounts, focus has left that control (on QA it was on
   * `<body>`), so there is no opener to go back to. The opener, when there is one, still wins.
   * Photo Review's "Save with no observations?" confirm is the case (AKL-883, QA Swarm UX-002).
   */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
};

/**
 * The overlay and panel of a modal dialog, with the keyboard and screen-reader behaviour
 * every modal in the app owes its users (AKL-883).
 *
 * Renders `modalOverlay` > `modalPanel` and puts the children inside the panel; the consumer
 * keeps its own header row, body and footer. The panel is a `role="dialog"` with
 * `aria-modal`, named by its visible title. On mount it remembers what had focus, moves focus
 * to its first enabled control, and on unmount hands focus back to that element if it is
 * still in the document (or, when focus was only on `<body>`, to `returnFocusRef`). While
 * mounted: Tab and Shift+Tab wrap at the ends of the enabled controls; Escape calls `onClose` unless `busy`, and stops propagating so nothing underneath
 * acts on it.
 *
 * Keys are answered from two places, because focus is not always inside the panel. The
 * panel's own `onKeyDown` handles everything pressed on a control inside it. A `document`
 * listener, held only by the most recently mounted dialog, handles a key whose target is
 * **outside** the panel — `<body>`, where focus lands when the focused control unmounts or
 * is disabled (the Check button in Import from CSV disappears once the job arrives; Start
 * export does the same), or after a click on the overlay. From there Tab is sent to the
 * first (Shift: last) control and Escape closes, instead of the Tab leaking to the page
 * behind the overlay and the Escape going nowhere.
 *
 * Two things layered inside a dialog own their own keys, and this panel stands aside for
 * them: a nested `role="dialog"` (the Role permissions info panel inside Invite User, or a
 * ConfirmationModal rendered inside another modal), and an open react-select menu
 * (`role="listbox"`) inside the panel, whose Escape closes the menu and not the dialog — the
 * same rule `ReviewPageFilter` applies. No consumer portals its menus; a `SelectInput` with
 * `usePortal` would need the listbox check widened to the portal target.
 *
 * Mount is open. Every consumer renders its dialog only while open (`if (!isOpen) return
 * null`), so the mount effect is the open transition and its cleanup is the close; a consumer
 * that kept this mounted while closed would show the overlay, so the assumption cannot rot
 * unseen.
 *
 * Respects the top layer without claiming it: while `isTopLayerOpen()` (Report a problem is
 * open above this dialog) the document fallback stands aside, since that dialog is mounted
 * globally as a sibling and neither the stack nor the descendant check can see it.
 *
 * Deliberately does **not** claim the top layer (`pushTopLayer`). `FullScreenImageReview.
 * handleSurfaceKey` returns on `isTopLayerOpen()` before it reaches the branch that treats
 * `B` as the confirm hotkey on the zero-observation and mark-blank confirms (AKL-708), so a
 * ConfirmationModal that claimed the top layer would silently kill that key. The review
 * surface already gates its own shortcuts on its own confirm state; `ReportProblemModal`
 * needs the top layer because it opens over dialogs that do not.
 *
 * Does not close on a backdrop click: none of the dialogs it replaced did.
 *
 * @param props see `ModalDialogProps`
 */
const ModalDialog: React.FC<ModalDialogProps> = ({
  onClose,
  busy = false,
  labelledBy,
  label,
  overlayClassName,
  panelClassName,
  testId,
  returnFocusRef,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Read on close by the mount-once effect below, so it goes through a ref like `answerKey`.
  const returnFocusRefRef = useRef(returnFocusRef);
  returnFocusRefRef.current = returnFocusRef;

  /**
   * Escape and the Tab trap. Called from the panel's React handler and from the document
   * fallback alike, so the two cannot drift.
   * @param event the key, from either event system
   */
  const answerKey = (event: KeyLike) => {
    const panel = panelRef.current;
    if (!panel) return;
    const target = event.target instanceof Element ? event.target : null;
    // A dialog layered inside this one owns the key — its own handler answers it.
    const owningDialog = target?.closest('[role="dialog"]');
    if (owningDialog && owningDialog !== panel) return;

    if (event.key === "Escape") {
      event.stopPropagation();
      // An open dropdown menu owns this Escape; it closes the menu, and the next one closes
      // the dialog. It cannot use `defaultPrevented`: react-select 5.x prevents Escape even
      // with its menu closed. Scoped to the panel, so a listbox elsewhere on the page can
      // never make Escape silently do nothing here.
      if (panel.querySelector('[role="listbox"]')) return;
      if (busy) return;
      onClose();
      return;
    }

    if (event.key !== "Tab") return;
    const focusable = focusableWithin(panel);
    // Nothing enabled to land on — a confirm while `saving`, Import from CSV while `busy`,
    // where every control is disabled. Hold focus on the panel rather than let the Tab
    // leave for the page behind the overlay (Copilot, PR #813 review; same handling as
    // `EndUploadModal`).
    if (focusable.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    // Focus on the panel itself (right after opening, or after a click on non-focusable text)
    // or on <body> is outside the set; an identity-only check would let this Tab escape into
    // the page behind the overlay.
    if (!active || focusable.indexOf(active) === -1) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };
  // The document listener is bound once per mount but must call the latest `answerKey`
  // (which closes over the current `busy` and `onClose`), so it reads it through a ref.
  const answerKeyRef = useRef(answerKey);
  answerKeyRef.current = answerKey;

  // Focus in on open, back out on close, and the document fallback while mounted. Reads
  // nothing from component scope except refs, so the empty dependency array is honest; the
  // opener lives in a local because the cleanup closes over exactly the value this run
  // captured.
  useEffect(() => {
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const panel = panelRef.current;
    if (panel) {
      mountedPanels.push(panel);
      const [first] = focusableWithin(panel);
      (first ?? panel).focus();
    }
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (!panel || mountedPanels[mountedPanels.length - 1] !== panel) return;
      // A top-layer dialog (Report a problem, mounted globally as a sibling, so neither the
      // stack nor the descendant check can see it) owns the keyboard while open — the same
      // guard the review surface's listeners apply (AKL-798; Copilot, PR #813 review).
      if (isTopLayerOpen()) return;
      // Inside the panel, the React handler has already answered.
      if (event.target instanceof Node && panel.contains(event.target)) return;
      // A dialog nested inside this one is open (the Role permissions info panel, which is
      // not a ModalDialog and so not on the stack above). It has its own document listener
      // for exactly this case; a key that reached <body> is its to answer, not ours
      // (Copilot, PR #813 review).
      if (panel.querySelector('[role="dialog"]')) return;
      answerKeyRef.current(event);
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => {
      document.removeEventListener("keydown", onDocumentKeyDown);
      if (panel) {
        const index = mountedPanels.indexOf(panel);
        if (index !== -1) mountedPanels.splice(index, 1);
      }
      // The opener, unless it left the document or was only `<body>`; then the caller's
      // fallback, if it named one and it is still here.
      const fallback = returnFocusRefRef.current?.current ?? null;
      const target =
        opener && opener.isConnected && opener !== document.body
          ? opener
          : fallback && fallback.isConnected
            ? fallback
            : null;
      target?.focus();
    };
  }, []);

  /**
   * A click on a non-focusable part of the panel (the title, helper text) moves focus to
   * `<body>`. Pull it back onto the panel so the next key is handled from inside it. Decided
   * by where focus actually landed, not by what was clicked: a click on a react-select option
   * keeps focus on the select's input, which is inside the panel and must be left alone.
   */
  const handleClick = () => {
    const panel = panelRef.current;
    if (!panel) return;
    if (!panel.contains(document.activeElement)) panel.focus();
  };

  return (
    <div className={`${tailwindClassnames.modalOverlay} ${overlayClassName}`}>
      <div
        ref={panelRef}
        className={`${tailwindClassnames.modalPanel} ${panelClassName}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={label}
        tabIndex={-1}
        onKeyDown={answerKey}
        onClick={handleClick}
        data-testid={testId}
      >
        {children}
      </div>
    </div>
  );
};

export default ModalDialog;
